// Sprint 6 (Phase 5, D39) — Derive a progress state from TodoWrite emits.
//
// The dashboard surfaces a sticky progress bar at the top of the chat
// whenever the active agent has written at least one TodoWrite plan. We
// take the LATEST emit (CLI semantics: each TodoWrite replaces the prior
// list) and project it into a small struct the UI can render.
//
// `derive-progress` is pure and deterministic; it does not consult agent
// status, message timestamps, or the partial buffer. The latest TodoWrite
// is the single source of truth.

import type { Message } from '../../types';
import { asToolUse } from '../messages';

export interface Todo {
  content: string;
  activeForm?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
}

export interface ProgressState {
  current: number;
  total: number;
  /** Headline of the in-progress todo (or activeForm if present). */
  inProgressItem: string | null;
  pct: number;
  fullList: Todo[];
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

export function parseTodos(input: Record<string, unknown>): Todo[] {
  const raw = input.todos;
  if (!Array.isArray(raw)) return [];
  const out: Todo[] = [];
  for (const t of raw) {
    if (!isObj(t)) continue;
    const content = typeof t.content === 'string' ? t.content : '';
    if (!content) continue;
    const statusRaw = typeof t.status === 'string' ? t.status : 'pending';
    const status: Todo['status'] = (
      ['pending', 'in_progress', 'completed', 'cancelled'].includes(statusRaw)
        ? statusRaw
        : 'pending'
    ) as Todo['status'];
    const todo: Todo = { content, status };
    if (typeof t.activeForm === 'string') todo.activeForm = t.activeForm;
    out.push(todo);
  }
  return out;
}

/** Walk the message stream and collect TodoWrite emits in order. */
export function collectTodoEmits(messages: Message[]): Message[] {
  const out: Message[] = [];
  for (const m of messages) {
    if (m.type !== 'tool_use') continue;
    const tu = asToolUse(m);
    if (tu.name === 'TodoWrite') out.push(m);
  }
  return out;
}

export function deriveProgress(messages: Message[]): ProgressState | null {
  const emits = collectTodoEmits(messages);
  if (emits.length === 0) return null;
  const last = emits[emits.length - 1];
  if (!last) return null;
  const tu = asToolUse(last);
  const todos = parseTodos(tu.input);
  if (todos.length === 0) return null;
  // Cancelled items don't count toward the denominator (they were dropped).
  const active = todos.filter((t) => t.status !== 'cancelled');
  const total = active.length;
  if (total === 0) return null;
  const current = active.filter((t) => t.status === 'completed').length;
  const inProgress = todos.find((t) => t.status === 'in_progress');
  const inProgressItem = inProgress
    ? inProgress.activeForm ?? inProgress.content
    : null;
  return {
    current,
    total,
    inProgressItem,
    pct: total === 0 ? 0 : current / total,
    fullList: todos,
  };
}
